import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  TrendingUp,
  TrendingDown,
  Package,
  ShoppingCart,
  DollarSign,
  Users,
  Truck,
  Plus,
  Factory,
  ArrowRight
} from 'lucide-react';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/dashboard');
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-KE').format(num || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#1B4D3E]">Dashboard</h1>
          <p className="text-slate-500 mt-1">Today's Overview</p>
        </div>
        <div className="sm:text-right">
          <p className="text-xs sm:text-sm text-slate-500">
            {new Date().toLocaleDateString('en-KE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={() => navigate('/purchases')}
          className="btn-primary gap-2"
          data-testid="quick-add-purchase"
        >
          <Plus className="h-4 w-4" />
          Add Purchase
        </Button>
        <Button
          onClick={() => navigate('/orders')}
          className="btn-primary gap-2"
          data-testid="quick-add-order"
        >
          <Plus className="h-4 w-4" />
          Add Order
        </Button>
        <Button
          onClick={() => navigate('/expenses')}
          className="btn-secondary gap-2"
          data-testid="quick-add-expense"
        >
          <Plus className="h-4 w-4" />
          Add Expense
        </Button>
        <Button
          onClick={() => navigate('/production')}
          className="btn-secondary gap-2"
          data-testid="quick-add-production"
        >
          <Plus className="h-4 w-4" />
          Add Production
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="card-interactive" data-testid="kpi-sales">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="kpi-label">Today's Sales</p>
                <p className="kpi-value mt-2">{formatCurrency(data?.today_sales)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-xl">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-interactive" data-testid="kpi-purchases">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="kpi-label">Today's Purchases</p>
                <p className="kpi-value mt-2">{formatCurrency(data?.today_purchases)}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-interactive" data-testid="kpi-expenses">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="kpi-label">Today's Expenses</p>
                <p className="kpi-value mt-2">{formatCurrency(data?.today_expenses)}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-xl">
                <DollarSign className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-interactive" data-testid="kpi-profit">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="kpi-label">Today's Net Profit</p>
                <p className={`kpi-value mt-2 ${data?.today_net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(data?.today_net_profit)}
                </p>
              </div>
              <div className={`p-3 rounded-xl ${data?.today_net_profit >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                {data?.today_net_profit >= 0 ? (
                  <TrendingUp className="h-6 w-6 text-green-600" />
                ) : (
                  <TrendingDown className="h-6 w-6 text-red-600" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inventory & Balances */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inventory Card */}
        <Card data-testid="inventory-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-heading">Inventory Status</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/inventory')} className="gap-1">
              View All <ArrowRight className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Raw Maize */}
            <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Package className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-700">Raw Maize</p>
                  <p className="text-sm text-slate-500">Stock available</p>
                </div>
              </div>
              <p className="text-xl font-bold text-amber-700">{formatNumber(data?.raw_maize_kg)} kg</p>
            </div>

            {/* Finished Goods */}
            <div className="p-4 bg-green-50 rounded-xl">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Factory className="h-5 w-5 text-green-600" />
                </div>
                <p className="font-medium text-slate-700">Finished Goods</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {data?.finished_goods?.length > 0 ? (
                  data.finished_goods.map((fg, idx) => (
                    <div key={idx} className="text-center p-2 bg-white rounded-lg">
                      <p className="text-lg font-bold text-green-700">{fg.quantity_bales || 0}</p>
                      <p className="text-xs text-slate-500">{fg.pack_size_kg}kg bales</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500 col-span-full text-center">No stock</p>
                )}
              </div>
            </div>

            {/* Byproducts */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Package className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-700">Byproducts</p>
                  <p className="text-sm text-slate-500">Stock available</p>
                </div>
              </div>
              <p className="text-xl font-bold text-slate-700">{formatNumber(data?.byproducts_kg)} kg</p>
            </div>
          </CardContent>
        </Card>

        {/* Outstanding Balances */}
        <Card data-testid="balances-card">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Outstanding Balances</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Sales Agent Outstanding */}
            <div
              className="flex items-center justify-between p-4 bg-orange-50 rounded-xl cursor-pointer hover:bg-orange-100 transition-colors"
              onClick={() => navigate('/agents')}
              data-testid="agent-balances-card"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Users className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-700">Sales Agent Balances</p>
                  <p className="text-sm text-slate-500">Amount receivable from agents</p>
                </div>
              </div>
              <p className="text-xl font-bold text-orange-600">{formatCurrency(data?.agent_outstanding)}</p>
            </div>

            {/* Supplier Outstanding */}
            <div
              className="flex items-center justify-between p-4 bg-blue-50 rounded-xl cursor-pointer hover:bg-blue-100 transition-colors"
              onClick={() => navigate('/suppliers')}
              data-testid="supplier-balances-card"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Truck className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-700">Supplier Balances</p>
                  <p className="text-sm text-slate-500">Amount payable to suppliers</p>
                </div>
              </div>
              <p className="text-xl font-bold text-blue-600">{formatCurrency(data?.supplier_outstanding)}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
